import type {
	PbLineItem,
	PbLineItemPackage,
	PbPackageProduct,
} from '@/apiTypes'
import type Plan from '@/classes/Plan'
import type { PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import { type OapDetails, oapProviders } from '@/data/oapProviders'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import {
	isCartSubItem,
	isPbLineItemPackage,
	isPbLineItemProduct,
	isPbLineItemProductAndPricingOptions,
} from '@/guards'
import { usePresentationLocale } from '@/i18n'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import type { CartItem, CartSubItem } from '@/types'
import { computed } from 'vue'

const defaultEverplansPrice = 299

/** CartSubItem with included lineItem */
export type CartSubItemAndLineItem = CartSubItem & {
	lineItem: PbLineItem | undefined
}

/** CartItem with included lineItem */
export type CartItemAndLineItem = CartItem & {
	lineItem: PbLineItem | undefined
	subItems: CartSubItemAndLineItem[]
}

export enum ManualAddCartItemType {
	Discount = 'discount',
	CustomItem = 'customItem',
}

export type ManualAddCartItem = {
	type: ManualAddCartItemType
	title: string
	description: string
	sku?: string
	amount: string
	guaranteed?: boolean
}

/** Extra information about the price of a single item */
export type PricingDetails = {
	price: number
	quantity?: number
	originalPrice: number

	guaranteed: number
	nonGuaranteed: number
}

/** Composable for merchandise helper methods */
export function useMerchandise() {
	const planStore = usePlanStore()
	const session = useSessionStore()
	const oapPlanProvider = useOapPlanProvider(session.activeOapProvider ?? null)
	const priceBook = computed(() => session.activePriceBook)
	const { n } = usePresentationLocale()

	/** Details about the current FH's OAP contract */
	const oapDetails = computed<OapDetails | null | undefined>(() => {
		const contract = session.activeFh?.oap?.data
		if (contract?.status !== 'Active') return null

		const provider = oapProviders[contract.carrier]

		const fullPrice = oapPlanProvider.getPlanByPaymentCount(1)?.price

		return contract && provider
			? {
					...contract,
					...provider,
					price: fullPrice ?? oapPlanProvider.defaultOapPrice,
					status: 'Active',
				}
			: null
	})

	function useOapPrice(plan: Plan) {
		return computed(
			() =>
				oapPlanProvider.getPlanById(plan.oapPayment.paymentPlanId)?.price ?? 0
		)
	}

	/** Fake product with details from the OAP provider and contract */
	const mockProductOap = computed<PbLineItemProductAndPricingOptions | null>(
		() => (oapDetails.value ? getMockOapProduct(oapDetails.value) : null)
	)

	/** Fake product for Everplans */
	const mockProductEverplans = computed(
		() =>
			({
				id: 'everplans',
				name: 'Everplans',
				category: PbProductType.Everplans,
				subcategory: PbSubcategory.Everplans,
				fixedPrice: defaultEverplansPrice,
				description: '',
				productImageUrl:
					'https://a-us.storyblok.com/f/1019754/593x417/4549cab150/everplans_logo.png',
				isGuaranteed: true,
				recordType: 'Product',
				pricingType: 'Fixed',
			}) as PbLineItemProductAndPricingOptions
	)

	// Everplans pricing is free if the plan has a package that grants it using isNoCostEverplans
	// else the cost comes from the defaultEverplansPrice
	const getEverplansPrice = (planId: string): number => {
		return shouldIncludeFreeEverplansPremium(planId) ? 0 : defaultEverplansPrice
	}

	function shouldIncludeFreeEverplansPremium(
		planId: string
	): CartItemAndLineItem | undefined {
		const cartItems = getPlanCartItems(planId) ?? []

		return cartItems.find((item) => item.lineItem?.isNoCostEverplans)
	}

	/** Get a fake product with the provided OAP details */
	function getMockOapProduct(
		oapDetails: OapDetails
	): PbLineItemProductAndPricingOptions {
		return {
			id: 'oap',
			name: `Out of Area Protection by ${oapDetails.name}`,
			category: PbProductType.Oap,
			fixedPrice: oapDetails.price,
			description: '',
			productImageUrl: oapDetails.imageUrl,
			isGuaranteed: true,
			recordType: 'Product',
			pricingType: 'Fixed',
		} as PbLineItemProductAndPricingOptions
	}
	/** Return a list of the CartItems in the plan, with the lineItem data included */
	function getPlanCartItems(planId: string): CartItemAndLineItem[] | undefined {
		const plan = planStore.getPlan(planId)

		return plan?.cartItems.map((i) => ({
			...i,
			lineItem: priceBook.value?.findLineItem(i.lineItemId),
			subItems: i.subItems.map((subItem) => ({
				...subItem,
				lineItem: priceBook.value?.findLineItem(subItem.lineItemId),
			})),
		}))
	}

	/** Format a price number as a string */
	function formatPrice(price: number | null | undefined) {
		if (
			price === undefined ||
			price === null ||
			typeof price !== 'number' ||
			Number.isNaN(price)
		) {
			return ''
		}

		return n(price, 'currency')
	}

	/** Get the CartItem or CartSubItem in the plan that matches the given parameters */
	function findLineItemInPlan(
		lineItem: PbLineItem,
		plan: Plan,
		price: number | null = null
	): CartItem | CartSubItem | undefined {
		const rootItem = plan.cartItems.find((item) => {
			// Ignore if soft deleted
			if (item.isSoftDeleted) return undefined

			// Match if same line item and price (ignore null price query)
			if (
				item.lineItemId === lineItem.id &&
				(price === null || item.price === price)
			) {
				return item
			}
		})

		if (rootItem) {
			return rootItem
		}

		// If not a match, do same search on subItems
		for (const item of plan.cartItems) {
			for (const subItem of item.subItems) {
				if (
					subItem.lineItemId === lineItem.id &&
					!subItem.isSoftDeleted &&
					(price === null || subItem.price === price)
				) {
					return subItem
				}
			}
		}
	}

	/** Get the CartItem or CartSubItem in the plan (including soft deleted items) */
	function findLineItemInPlanIncludingSoftDeleted(
		lineItem: PbLineItem,
		plan: Plan,
		price: number | null = null
	): CartItem | CartSubItem | undefined {
		const rootItem = plan.cartItems.find(
			(item) =>
				item.lineItemId === lineItem.id &&
				(price === null || item.price === price)
		)
		if (rootItem) return rootItem

		for (const item of plan.cartItems) {
			const subItem = item.subItems.find(
				(sub) =>
					sub.lineItemId === lineItem.id &&
					(price === null || sub.price === price)
			)
			if (subItem) return subItem
		}
	}

	/** Returns true if the given line item can be found in the plan by id */
	function isLineItemInPlan(
		lineItem: PbLineItem,
		plan: Plan,
		price: number | null = null
	) {
		return undefined !== findLineItemInPlan(lineItem, plan, price)
	}

	/** Get the price of a cart item to display in the cart */
	function getCartItemPrice(
		plan: Plan,
		cartItem: CartItemAndLineItem | CartSubItemAndLineItem
	): PricingDetails {
		const quantity = cartItem.quantity ?? 1

		if (!cartItem.lineItem) {
			throw new Error('Line item not found')
		}

		// Use override price if available
		if (cartItem.price !== null) {
			const price = cartItem.price * quantity

			return {
				price,
				originalPrice: cartItem.price * quantity,
				quantity,
				guaranteed: cartItem.lineItem.isGuaranteed ? price : 0,
				nonGuaranteed: cartItem.lineItem.isGuaranteed ? 0 : price,
			}
		}

		// Use same logic as getPlanLineItemPrice if it is not a package
		if (isPbLineItemProduct(cartItem.lineItem)) {
			const base = getPlanLineItemPrice(plan, cartItem.lineItemId)

			if (!base) {
				throw new Error('Cannot find base price for cart item')
			}

			const price = base.price * quantity

			return {
				price,
				originalPrice: base.originalPrice * quantity,
				quantity,
				guaranteed: cartItem.lineItem.isGuaranteed ? price : 0,
				nonGuaranteed: cartItem.lineItem.isGuaranteed ? 0 : price,
			}
		}

		if (!priceBook.value) {
			throw new Error('Price book not found')
		}

		if (isCartSubItem(cartItem)) {
			throw new Error('Cart item is a package and a sub-item')
		}

		return calculatePrice(cartItem)
	}

	/**
	 * Disposition sub-items (e.g. 'Direct Burial') carry the package's own price
	 * rather than an amount charged on top of it, so adding them to the package
	 * total charges for the package twice. The G&S form treats them the same way:
	 * disposition maps to a checkbox with no amount field.
	 */
	function isDispositionItem(lineItem: PbLineItem | undefined): boolean {
		return lineItem?.category === PbProductType.Disposition
	}

	/** What a sub-item adds to its package: its override price, else its list price */
	function getSubItemPrice(subItem: CartSubItemAndLineItem): number {
		return subItem.price ?? subItem.lineItem?.fixedPrice ?? 0
	}

	function sumSubItemPrices(
		subItems: CartSubItemAndLineItem[],
		filter: 'all' | 'guaranteed' | 'nonGuaranteed'
	): number {
		return subItems.reduce((total, subItem) => {
			if (
				filter === 'all' ||
				(filter === 'guaranteed' && subItem.lineItem?.isGuaranteed) ||
				(filter === 'nonGuaranteed' && !subItem.lineItem?.isGuaranteed)
			) {
				return total + getSubItemPrice(subItem)
			}

			return total
		}, 0)
	}

	/** An all-zero result, for when there is no price book to price against */
	function zeroPricingDetails(): PricingDetails {
		return {
			price: 0,
			originalPrice: 0,
			quantity: 1,
			guaranteed: 0,
			nonGuaranteed: 0,
		}
	}

	function getNonPackagePrice(cartItem: CartItemAndLineItem): PricingDetails {
		const price = cartItem.lineItem?.fixedPrice || cartItem.price || 0

		return {
			price,
			originalPrice: price,
			quantity: 1,
			guaranteed: cartItem.lineItem?.isGuaranteed ? price : 0,
			nonGuaranteed: cartItem.lineItem?.isGuaranteed ? 0 : price,
		}
	}

	function getPackageTotal(cartItem: CartItemAndLineItem): PricingDetails {
		const originalPrice = calculatePackageTotal(cartItem.lineItemId)
		const packagePrice = cartItem.lineItem?.fixedPrice ?? 0
		const subItems: CartSubItemAndLineItem[] = cartItem.subItems ?? []
		const chargeableSubItems = subItems.filter(
			(subItem) => !isDispositionItem(subItem.lineItem)
		)

		return {
			price: packagePrice + sumSubItemPrices(chargeableSubItems, 'all'),
			originalPrice,
			guaranteed:
				(cartItem.lineItem?.isGuaranteed ? packagePrice : 0) +
				sumSubItemPrices(chargeableSubItems, 'guaranteed'),
			nonGuaranteed:
				(cartItem.lineItem?.isGuaranteed ? 0 : packagePrice) +
				sumSubItemPrices(chargeableSubItems, 'nonGuaranteed'),
		}
	}

	function calculatePrice(cartItem: CartItemAndLineItem): PricingDetails {
		if (!priceBook.value) {
			return zeroPricingDetails()
		}

		if (cartItem.lineItem?.category !== PbProductType.Package) {
			return getNonPackagePrice(cartItem)
		}

		return getPackageTotal(cartItem)
	}

	/**
	 * Get the price to show on a product page for a particular line item.
	 * The price may be lower if a plan already includes a
	 * package with that item or a stand-in for the item (to match by tag).
	 */
	function getPlanLineItemPrice(
		plan: Plan,
		lineItemId: string
	): PricingDetails {
		if (!priceBook.value) throw new Error('Price book not found')

		const lineItem = priceBook.value.findLineItem(lineItemId)
		if (!lineItem) throw new Error('Line item not found: ' + lineItemId)

		// Check for package or bundle
		if (!isPbLineItemProductAndPricingOptions(lineItem)) {
			const pkg = lineItem as PbLineItemPackage
			const packageTotal = calculatePackageTotal(pkg.id)

			// If the item is a Package or Bundle, its price will not change
			return {
				price: packageTotal,
				originalPrice: packageTotal,
				guaranteed: lineItem.isGuaranteed ? lineItem.fixedPrice : 0,
				nonGuaranteed: lineItem.isGuaranteed ? 0 : lineItem.fixedPrice,
			}
		}

		let price = lineItem.fixedPrice
		let originalPrice = lineItem.fixedPrice

		// Search items in the cart
		for (const cartItem of plan.cartItems) {
			if (cartItem.isSoftDeleted) continue

			const cartLineItem = priceBook.value?.findLineItem(cartItem.lineItemId)
			if (!cartLineItem) throw new Error('Line item not found')

			// If package sub-item is the product or a stand-in for the product,
			// use the sub-item price if lower
			if (qualifiesForDiscount(lineItem) && isPbLineItemPackage(cartLineItem)) {
				for (const subCartItem of cartItem.subItems) {
					if (subCartItem.isSoftDeleted) continue

					const subLineItem = priceBook.value?.findProduct(
						subCartItem.lineItemId
					)
					if (!subLineItem) {
						console.error(subCartItem)
						throw new Error(
							'Product sub line item not found. Is there a package inside a package?',
							{ cause: subCartItem }
						)
					}

					// If package item price was set to 0, discount the entire price.
					// Otherwise, the first value we find is the amount to subtract.
					const discount =
						subCartItem.price === 0
							? lineItem.fixedPrice
							: (subCartItem.price ?? subLineItem.fixedPrice)

					if (
						discount != null &&
						(subLineItem.id === lineItem.id ||
							(subLineItem.isStandIn &&
								doLineItemsShareTag(subLineItem, lineItem)))
					) {
						const newPrice = Math.max(0, originalPrice - discount)
						price = Math.min(price, newPrice)
					}
				}
			}
		}

		return {
			price,
			originalPrice,
			guaranteed: lineItem.isGuaranteed ? price : 0,
			nonGuaranteed: lineItem.isGuaranteed ? 0 : price,
		}
	}

	/** Returns true if the line item should be discounted by a matching product in a package. */
	function qualifiesForDiscount(lineItem: PbLineItem) {
		if (lineItem.subcategory == null) return false

		return [
			PbSubcategory.MerchandiseCasket,
			PbSubcategory.MerchandiseUrn,
		].includes(lineItem.subcategory)
	}

	/** Get the subtotals for a plan's entire cart */
	function calculateCartTotals(plan: Plan): {
		guaranteedFuneralSubtotal: number
		nonGuaranteedFuneralSubTotal: number
		funeralTotal: number
		oapSubTotal: number
		everplansSubTotal: number
		grandTotal: number
	} {
		let guaranteedFuneralSubtotal = 0
		let nonGuaranteedFuneralSubTotal = 0
		const oapSubTotal = mockProductOap.value?.fixedPrice ?? 0
		const everplansSubTotal = getEverplansPrice(plan.id)
		const cartItems = getPlanCartItems(plan.id) ?? []
		const customItems = plan.customCartItems

		// Add up all the cart items
		cartItems
			.filter((item) => !item.isSoftDeleted)
			.forEach((item) => {
				const price = getCartItemPrice(plan, item)

				guaranteedFuneralSubtotal += price.guaranteed
				nonGuaranteedFuneralSubTotal += price.nonGuaranteed
			})

		// Add up the custom items
		for (let i = 0; i < customItems.length; i++) {
			if (customItems[i].isSoftDeleted) continue

			if (customItems[i].guaranteed) {
				guaranteedFuneralSubtotal += customItems[i].price
			} else {
				nonGuaranteedFuneralSubTotal += customItems[i].price
			}
		}

		return {
			guaranteedFuneralSubtotal,
			nonGuaranteedFuneralSubTotal,
			funeralTotal: guaranteedFuneralSubtotal + nonGuaranteedFuneralSubTotal,
			oapSubTotal,
			everplansSubTotal,
			grandTotal:
				guaranteedFuneralSubtotal +
				nonGuaranteedFuneralSubTotal +
				(plan.oapIncluded ? oapSubTotal : 0) +
				(plan.everplansIncluded ? everplansSubTotal : 0),
		}
	}

	/** Returns true if the given line item has the given tag */
	function doesLineItemHaveTag(
		lineItem: PbLineItemProductAndPricingOptions,
		tagId: string
	) {
		return lineItem.tags.some((tag) => tag.id === tagId)
	}

	/** Returns true if the two line items have at least one tag in common */
	function doLineItemsShareTag(
		lineItem1: PbLineItemProductAndPricingOptions,
		lineItem2: PbLineItemProductAndPricingOptions
	) {
		return lineItem1.tags.some((tag) =>
			lineItem2.tags.some((t) => t.id === tag.id)
		)
	}

	function getPlanImage(planId: string): string | undefined {
		const cartItems = getPlanCartItems(planId) ?? []

		if (cartItems.length <= 0) return

		// If there is a non-Essentials package then show package image
		const pkg = getNonEssentialPackageCartItem(cartItems)

		if (pkg) return pkg.lineItem?.productImageUrl ?? undefined

		// If no non-Essentials package then find casket
		const casket = cartItems.find(
			(c) => c.lineItem?.subcategory === PbSubcategory.MerchandiseCasket
		)
		if (casket) return casket.lineItem?.productImageUrl ?? undefined

		// If no casket then find urn
		const urn = cartItems.find(
			(c) => c.lineItem?.subcategory === PbSubcategory.MerchandiseUrn
		)
		if (urn) return urn.lineItem?.productImageUrl ?? undefined

		// If no package, casket, or urn then find most expensive one

		let topPrice = -1
		let imageURL = cartItems[0].lineItem?.productImageUrl
		for (let i = 0; i < cartItems.length; i++) {
			if ((cartItems[i].lineItem?.fixedPrice ?? 0) > topPrice) {
				topPrice = cartItems[i].lineItem?.fixedPrice ?? 0
				imageURL = cartItems[i].lineItem?.productImageUrl
			}
		}
		if (imageURL) return imageURL

		// Fallback to Essentials package image if it is available and nothing else is
		const essentials = cartItems.find(
			(c) => c.lineItem?.subcategory === PbSubcategory.PackageEssentials
		)
		if (essentials) return essentials.lineItem?.productImageUrl ?? undefined
	}

	/** Get the estimated total affected by inflation */
	function calculateInflationEstimate(
		total: number,
		year: number,
		rate: number | undefined
	): number {
		if (rate === undefined) return total
		return total * Math.pow((100 + rate) / 100, year)
	}

	/**
	 * Returns the same package product but with the matching line item attached.
	 */
	function addLineItemToPackageProduct(
		packageProduct: PbPackageProduct
	): PbPackageProduct & { lineItem: PbLineItem | undefined } {
		const currentLineItem = priceBook.value?.findLineItem(
			packageProduct.refSpbLineProduct
		)
		return { ...packageProduct, lineItem: currentLineItem }
	}

	/** Find the parent package for the given sub-item */
	function findParentPackageForSubItem(
		plan: Plan,
		subItemLineItemId: string
	): { parent: CartItem; subItem: CartSubItem } | undefined {
		for (const parent of plan.cartItems) {
			for (const sub of parent.subItems) {
				if (!sub.isSoftDeleted && sub.lineItemId === subItemLineItemId) {
					return { parent, subItem: sub }
				}
			}
		}
	}

	/**
	 * Return the subitems of an essentials package
	 * Get only the essentials package subitems that should appear
	 * in the cart (non-virtual) and put them in the correct order.
	 */
	function getEssentialsPackageSubItems(
		packageId: string,
		subItems: CartSubItemAndLineItem[]
	): CartSubItemAndLineItem[] {
		const book = priceBook.value
		if (!book) return []

		// Build package order map
		const orderIndex = new Map(
			(
				book.getSortedPackageProducts(
					book.getProductsInPackage(packageId) ?? [],
					true
				) ?? []
			).map((p, i) => [p.refSpbLineProduct, i])
		)

		// Filter out virtual subItems and sort by package order
		return subItems
			.filter((item) => !item.lineItem?.isVirtualLine)
			.sort(
				(a, b) =>
					(orderIndex.get(a.lineItemId) ?? 999) -
					(orderIndex.get(b.lineItemId) ?? 999)
			)
	}

	/**
	 * Return the new package total
	 * The package total is the fixedPrice of the package plus the prices of the subitems
	 */
	function calculatePackageTotal(packageId: string): number {
		const packageLineItem = priceBook?.value?.findLineItem(packageId)

		if (!priceBook.value || !packageLineItem) {
			return 0
		}

		const subItems = priceBook.value?.getProductsInPackage(packageId) ?? []
		const subItemsTotal = subItems.reduce((acc, item) => {
			if (isDispositionItem(item.lineItem)) {
				return acc
			}

			return acc + (item?.fixedPrice ?? 0)
		}, 0)

		return packageLineItem.fixedPrice + subItemsTotal
	}

	function getNonEssentialPackageCartItem(
		cartItems: CartItemAndLineItem[]
	): CartItemAndLineItem | undefined {
		return cartItems.find(
			(c) =>
				c.lineItem?.category === PbProductType.Package &&
				c.lineItem?.subcategory !== PbSubcategory.PackageEssentials
		)
	}

	return {
		getEverplansPrice,
		calculateCartTotals,
		calculatePackageTotal,
		calculateInflationEstimate,
		doesLineItemHaveTag,
		findLineItemInPlan,
		findLineItemInPlanIncludingSoftDeleted,
		formatPrice,
		getCartItemPrice,
		getMockOapProduct,
		mockProductEverplans,
		getPlanCartItems,
		getPlanImage,
		getPlanLineItemPrice,
		isLineItemInPlan,
		mockProductOap,
		oapDetails,
		useOapPrice,
		priceBook,
		doLineItemsShareTag,
		qualifiesForDiscount,
		getEssentialsPackageSubItems,
		findParentPackageForSubItem,
		addLineItemToPackageProduct,
		shouldIncludeFreeEverplansPremium,
	}
}
